import { apiClient, handleApiSuccess } from './api';
import { PendingStudent } from '../types';

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: 'Student' | 'Faculty' | 'Ambassador';
  branch?: string;
  profilePicture?: string;
  mobileNumber?: string;
  rollNumber?: string;
  isVerified: boolean;
  emailVerified?: boolean;
  clubs: Array<{
    id: string;
    name: string;
    logo: string;
    memberRole: string;
  }>;
  badges: Array<{
    id: string;
    title: string;
    description: string;
    icon: string;
    awardedBy: string;
    dateAwarded: string;
  }>;
  goals?: string;
  collegeId?: string;
  collegeName?: string;
  aiApiKey?: string;
  permissions?: {
    allowedBranches: string[];
    canApproveStudents: boolean;
    canManageClubs: boolean;
    canViewAnalytics: boolean;
    canManagePosts: boolean;
  };
}

export interface UserSearchResult {
  id: string;
  name: string;
  email: string;
  role: 'Student' | 'Faculty';
  branch?: string;
  profilePicture?: string;
  clubCount?: number;
}

export interface SearchUsersResponse {
  users: UserSearchResult[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

/**
 * User Service
 * Handles all user-related API calls
 */
export class UserService {
  /**
   * Search users by name or email
   */
  static async searchUsers(
    query: string,
    page: number = 1,
    limit: number = 10
  ): Promise<SearchUsersResponse> {
    const response = await apiClient.get(`/users/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`);
    const data = handleApiSuccess(response);
    return {
      users: data,
      pagination: {
        page,
        limit,
        total: data.length
      }
    };
  }

  /**
   * Get user profile by ID
   */
  static async getUserProfile(userId: string): Promise<UserProfile> {
    const response = await apiClient.get(`/users/profile/${userId}`);
    return handleApiSuccess(response);
  }

  /**
   * Get current user profile
   */
  static async getCurrentUserProfile(): Promise<UserProfile> {
    const response = await apiClient.get('/users/me');
    return handleApiSuccess(response);
  }

  /**
   * Update user profile
   */
  static async updateUserProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    const response = await apiClient.put('/users/me', data);
    return handleApiSuccess(response);
  }

  /**
   * Get user suggestions (for autocomplete)
   */
  static async getUserSuggestions(query: string, limit: number = 5): Promise<UserSearchResult[]> {
    const response = await apiClient.get(`/users/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    const data = handleApiSuccess(response);
    return data;
  }

  /**
   * Get current user (for context)
   */
  static async getCurrentUser(): Promise<UserProfile> {
    const response = await apiClient.get('/users/me');
    return handleApiSuccess(response);
  }

  /**
   * Get pending students for approval
   */
  static async getPendingStudents(): Promise<PendingStudent[]> {
    const response = await apiClient.get('/users/pending');
    const data = handleApiSuccess(response);
    return data;
  }

  /**
   * Get approved students
   */
  static async getApprovedStudents(): Promise<PendingStudent[]> {
    const response = await apiClient.get('/users/approved');
    const data = handleApiSuccess(response);
    return data;
  }

  /**
   * Get rejected students
   */
  static async getRejectedStudents(): Promise<PendingStudent[]> {
    const response = await apiClient.get('/users/rejected');
    const data = handleApiSuccess(response);
    return data;
  }

  /**
   * Approve a student
   */
  static async approveStudent(studentId: string): Promise<void> {
    const response = await apiClient.post(`/users/${studentId}/approve`);
    return handleApiSuccess(response);
  }

  /**
   * Reject a student
   */
  static async rejectStudent(studentId: string, reason?: string): Promise<void> {
    const response = await apiClient.post(`/users/${studentId}/reject`, { reason });
    return handleApiSuccess(response);
  }

  /**
   * Reconsider a rejected student
   */
  static async reconsiderStudent(studentId: string): Promise<void> {
    const response = await apiClient.post(`/users/${studentId}/reconsider`);
    return handleApiSuccess(response);
  }

  /**
   * Update user goals
   */
  static async updateGoals(goals: string): Promise<UserProfile> {
    const response = await apiClient.put('/users/profile', { goals });
    return handleApiSuccess(response);
  }
}

export default UserService;